import React, { useState } from "react"
import { ArrowLeft, CheckCircle2, Link2Off } from "lucide-react"
import { Link } from "../router"
import { Button, ErrorNotice } from "../ui"
import { PasswordRecoveryLayout } from "./PasswordRecoveryLayout"

export function ResetPasswordPage() {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)
  const token = new URLSearchParams(window.location.search).get("token")

  async function submit(event) {
    event.preventDefault()
    setError(null)
    const values = Object.fromEntries(new FormData(event.currentTarget))
    if (values.password !== values.password_confirmation) {
      setError(new Error("Passwords do not match."))
      return
    }

    setBusy(true)
    try {
      const response = await fetch("/api/v1/auth/reset_password", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ...values, token })
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(resetErrorMessage(payload))
      window.history.replaceState({}, "", "/reset-password")
      setDone(true)
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <PasswordRecoveryLayout>
      {done ? (
        <div className="recovery-result" aria-live="polite">
          <span className="recovery-result__icon recovery-result__icon--success"><CheckCircle2 size={28} aria-hidden="true" /></span>
          <p className="eyebrow">Password updated</p>
          <h2>You’re ready to sign in</h2>
          <p>Your new password is active. For your security, any other signed-in sessions have been closed.</p>
          <Link className="button button--primary" to="/login">Continue to sign in</Link>
        </div>
      ) : token ? (
        <>
          <p className="eyebrow">Choose a new password</p>
          <h2>Reset your password</h2>
          <p>Use at least 8 characters. A longer, unique password is safest.</p>
          <ErrorNotice error={error} />
          <form className="form-stack" onSubmit={submit}>
            <label htmlFor="new-password">New password<input id="new-password" name="password" type="password" autoComplete="new-password" minLength="8" autoFocus required /></label>
            <label htmlFor="confirm-password">Confirm new password<input id="confirm-password" name="password_confirmation" type="password" autoComplete="new-password" minLength="8" required /></label>
            <Button type="submit" disabled={busy}>{busy ? "Updating…" : "Update password"}</Button>
          </form>
          <Link className="recovery-back" to="/login"><ArrowLeft size={15} aria-hidden="true" /> Back to sign in</Link>
        </>
      ) : (
        <div className="recovery-result">
          <span className="recovery-result__icon recovery-result__icon--warning"><Link2Off size={27} aria-hidden="true" /></span>
          <p className="eyebrow">Link unavailable</p>
          <h2>Request a new reset link</h2>
          <p>This password reset link is missing, invalid, expired, or has already been used.</p>
          <Link className="button button--primary" to="/forgot-password">Send a new reset link</Link>
          <Link className="recovery-back" to="/login"><ArrowLeft size={15} aria-hidden="true" /> Back to sign in</Link>
        </div>
      )}
    </PasswordRecoveryLayout>
  )
}

function resetErrorMessage(payload) {
  if (payload.error === "Reset link is invalid or expired") return "This reset link is invalid, expired, or has already been used. Request a new link to continue."
  const passwordErrors = payload.errors?.password || payload.errors?.password_confirmation
  if (passwordErrors?.length) return `Password ${passwordErrors.join(", ")}.`
  return payload.error || "We couldn’t update your password. Please try again."
}
